import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { useTranslation } from "react-i18next";
import { CodServedCustomer } from "../../../types/codSalesRep";
import { formatCodAmount } from "../../../utils/codUtils";
import CodInitialsAvatar from "../../../components/ui/CodInitialsAvatar";
import { iconBtnSx, NJIM } from "./codStyles";

interface CodSelectedCustomerCardProps {
  customer: CodServedCustomer | null;
  outstanding: number;
  onClear: () => void;
}

const miniStatSx = {
  boxSizing: "border-box",
  minWidth: 0,
  px: 1.5,
  py: 1,
  borderRadius: "12px",
  bgcolor: NJIM.white,
  border: `1px solid ${NJIM.border}`,
} as const;

/** Presentational header: which customer the page is scoped to. */
const CodSelectedCustomerCard: React.FC<CodSelectedCustomerCardProps> = ({
  customer,
  outstanding,
  onClear,
}) => {
  const { t } = useTranslation();

  if (!customer) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2, minWidth: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: NJIM.ink,
            color: NJIM.white,
            flexShrink: 0,
          }}
        >
          <GroupsRoundedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: NJIM.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {t("codSalesRep.overviewScope")}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: NJIM.ink }}>
            {t("codSalesRep.allCustomers")}
          </Typography>
        </Box>
      </Box>
    );
  }

  const address = [customer.addressDescription1, customer.addressDescription2]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" — ");
  const phone = customer.mobileNumber?.trim();

  return (
    <Box
      sx={{
        mb: 2.5,
        p: { xs: 1.5, sm: 2 },
        borderRadius: "16px",
        border: `1.5px solid ${NJIM.mint}`,
        background: NJIM.mintWash,
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr) auto", md: "minmax(0, 1.3fr) minmax(0, 1fr) auto" },
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <CodInitialsAvatar name={customer.customerName} size={44} selected />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: NJIM.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {t("codSalesRep.overviewScope")}
          </Typography>
          <Typography
            sx={{ fontWeight: 700, fontSize: 16, color: NJIM.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            title={customer.customerName}
          >
            {customer.customerName}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25, alignItems: "center" }}>
            <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
              {customer.customerCode}
              {customer.customerCategory ? ` · ${customer.customerCategory}` : ""}
            </Typography>
            {phone ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PhoneOutlinedIcon sx={{ fontSize: 14, color: NJIM.textSecondary }} />
                <Typography sx={{ fontSize: 12, color: NJIM.textPrimary }}>{phone}</Typography>
              </Box>
            ) : null}
            {address ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <PlaceOutlinedIcon sx={{ fontSize: 14, color: NJIM.textSecondary }} />
                <Typography sx={{ fontSize: 12, color: NJIM.textPrimary }}>{address}</Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1,
          gridColumn: { xs: "1 / -1", md: "auto" },
          gridRow: { xs: 2, md: "auto" },
        }}
      >
        <Box sx={miniStatSx}>
          <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600 }}>
            {t("codSalesRep.creditLimit")}
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: NJIM.ink, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
            {formatCodAmount(customer.availableCredit ?? 0, customer.currency)}
          </Typography>
        </Box>
        <Box sx={miniStatSx}>
          <Typography sx={{ fontSize: 11, color: NJIM.textSecondary, fontWeight: 600 }}>
            {t("codSalesRep.customerOutstanding")}
          </Typography>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 800,
              color: outstanding > 0 ? NJIM.danger : NJIM.success,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
            }}
          >
            {formatCodAmount(outstanding, customer.currency)}
          </Typography>
        </Box>
      </Box>

      <Tooltip title={t("codSalesRep.allCustomers")}>
        <IconButton
          aria-label={t("codSalesRep.changeCustomer")}
          onClick={onClear}
          sx={{ ...iconBtnSx, bgcolor: NJIM.white, alignSelf: { xs: "start", md: "center" } }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default CodSelectedCustomerCard;
